// src/stores/partnershipStore.js
import { makeAutoObservable, runInAction } from 'mobx'
import apiClient from '../utils/apiClient'
import logger from '../utils/logger'

class PartnershipStore {
  // Поиск производителей
  searchResults = []
  searchLoading = false
  searchError = null
  
  // Исходящие запросы (от нашего магазина)
  outgoingRequests = []
  outgoingLoading = false
  outgoingError = null
  
  // Активные партнерства
  activePartnerships = []
  partnershipsLoading = false
  partnershipsError = null
  
  // Общие состояния
  submitting = false
  error = null

  constructor() {
    makeAutoObservable(this)
  }

  // Поиск производителей для партнерства
  async searchManufacturers(query) {
    if (!query || query.trim().length < 2) {
      runInAction(() => {
        this.searchResults = []
        this.searchError = null
      })
      return
    }

    runInAction(() => {
      this.searchLoading = true
      this.searchError = null
    })

    try {
      const response = await apiClient.get('/partnerships/search-manufacturers', {
        params: { q: query.trim() }
      })
      
      runInAction(() => {
        this.searchResults = response.data.manufacturers
        this.searchLoading = false
      })

      logger.log('Поиск производителей завершен:', response.data.manufacturers.length)
      return response.data.manufacturers

    } catch (error) {
      runInAction(() => {
        this.searchError = error.response?.data?.error || 'Ошибка при поиске производителей'
        this.searchLoading = false
        this.searchResults = []
      })

      logger.error('Ошибка при поиске производителей:', error)
      throw error
    }
  }

  // Отправка запроса на партнерство
  async sendPartnershipRequest(manufacturerId) {
    runInAction(() => {
      this.submitting = true
      this.error = null
    })

    try {
      const response = await apiClient.post('/partnerships/request', {
        manufacturerId
      })
      
      runInAction(() => {
        this.submitting = false
        // Обновляем список исходящих запросов
        this.fetchOutgoingRequests()
      })

      logger.log('Запрос на партнерство отправлен:', response.data)
      return response.data

    } catch (error) {
      runInAction(() => {
        this.error = error.response?.data?.error || 'Ошибка при отправке запроса'
        this.submitting = false
      })

      logger.error('Ошибка при отправке запроса на партнерство:', error)
      throw error
    }
  }

  // Получение исходящих запросов
  async fetchOutgoingRequests() {
    runInAction(() => {
      this.outgoingLoading = true
      this.outgoingError = null
    })

    try {
      const response = await apiClient.get('/partnerships/outgoing')
      
      runInAction(() => {
        this.outgoingRequests = response.data.requests
        this.outgoingLoading = false
      })

      logger.log('Исходящие запросы загружены:', response.data.requests.length)
      return response.data.requests

    } catch (error) {
      runInAction(() => {
        this.outgoingError = error.response?.data?.error || 'Ошибка при загрузке запросов'
        this.outgoingLoading = false
      })

      logger.error('Ошибка при загрузке исходящих запросов:', error)
      throw error
    }
  }

  // Получение активных партнерств
  async fetchActivePartnerships() {
    runInAction(() => {
      this.partnershipsLoading = true
      this.partnershipsError = null
    })

    try {
      const response = await apiClient.get('/partnerships/active')
      
      runInAction(() => {
        this.activePartnerships = response.data.partnerships
        this.partnershipsLoading = false
      })

      logger.log('Активные партнерства загружены:', response.data.partnerships.length)
      return response.data.partnerships

    } catch (error) {
      runInAction(() => {
        this.partnershipsError = error.response?.data?.error || 'Ошибка при загрузке партнерств'
        this.partnershipsLoading = false
      })

      logger.error('Ошибка при загрузке активных партнерств:', error)
      throw error
    }
  }

  // Очистка результатов поиска
  clearSearch() {
    runInAction(() => {
      this.searchResults = []
      this.searchError = null
    })
  }

  // Очистка ошибок
  clearError() {
    runInAction(() => {
      this.error = null
      this.searchError = null
      this.outgoingError = null
      this.partnershipsError = null
    })
  }

  // Сброс состояния
  reset() {
    runInAction(() => {
      this.searchResults = []
      this.searchLoading = false
      this.searchError = null
      this.outgoingRequests = []
      this.outgoingLoading = false
      this.outgoingError = null
      this.activePartnerships = []
      this.partnershipsLoading = false
      this.partnershipsError = null
      this.submitting = false
      this.error = null
    })
  }

  // Получить статус партнерства с конкретным производителем
  getPartnershipStatus(manufacturerId) {
    // Проверяем активные партнерства
    const activePartnership = this.activePartnerships.find(
      p => p.manufacturer_id === manufacturerId
    )
    if (activePartnership) {
      return 'approved'
    }

    // Проверяем исходящие запросы
    const outgoingRequest = this.outgoingRequests.find(
      r => r.manufacturer_id === manufacturerId
    )
    if (outgoingRequest) {
      return outgoingRequest.status
    }

    return null
  }
}

const partnershipStore = new PartnershipStore()
export default partnershipStore 